import React, { useState } from "react";
import { C } from "../../../core/constants";
import { formatAnimal, elementColor } from "../coupleMatchVisuals";

export function SectionIntro({ children, style }) {
  if (!children) return null;
  return (
    <p style={{ color: C.textSecondary, fontSize: 13, lineHeight: 1.7, marginBottom: 16, ...style }}>
      {children}
    </p>
  );
}

export function LegendRow({ items = [], acc }) {
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            fontSize: 11,
            padding: "5px 10px",
            borderRadius: 999,
            border: `1px solid ${item.color || acc?.primary || C.textMuted}44`,
            color: item.color || C.textSecondary,
            background: `${item.color || acc?.primary || "#fff"}12`,
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
}

export function CollapsibleGuide({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 14 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: "none",
          color: C.textMuted,
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {open ? "−" : "+"} {title}
      </button>
      {open && (
        <div style={{ marginTop: 10, display: "grid", gap: 8, fontSize: 12, color: C.textSecondary, lineHeight: 1.65 }}>
          {children}
        </div>
      )}
    </div>
  );
}

export function InsightList({ title, items = [], accent }) {
  const list = items.filter(Boolean);
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      {title && (
        <div style={{ fontSize: 11, color: accent || C.textMuted, letterSpacing: "0.1em", marginBottom: 8 }}>
          {title}
        </div>
      )}
      <ul style={{ margin: 0, paddingLeft: 18, color: C.textSecondary, fontSize: 13, lineHeight: 1.7 }}>
        {list.map((item, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function YearDetailPanel({ yearItem, acc, onClose, locale = "en" }) {
  if (!yearItem?.narrative) return null;
  const toneColor = {
    auspicious: acc.secondary,
    caution: "#ff7b9c",
    neutral: C.textMuted,
  }[yearItem.tone];

  return (
    <div
      style={{
        marginTop: 14,
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${toneColor}44`,
        background: `${toneColor}10`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 18, color: C.textPrimary, fontFamily: "'Cormorant Garamond', serif" }}>
            {yearItem.year} · {yearItem.ganZhi} · {formatAnimal(yearItem.annualAnimal, locale)}
          </div>
          <div style={{ fontSize: 12, color: toneColor, marginTop: 4 }}>{yearItem.narrative.headline}</div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 18 }}
            aria-label="Close"
          >
            ×
          </button>
        )}
      </div>
      <p style={{ color: C.textSecondary, fontSize: 13, lineHeight: 1.75, margin: "0 0 10px" }}>
        {yearItem.narrative.text}
      </p>
      {yearItem.narrative.whyText && (
        <p style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.65, margin: "0 0 8px" }}>
          {yearItem.narrative.whyText}
        </p>
      )}
      {yearItem.narrative.focus && (
        <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.06em" }}>
          {yearItem.narrative.focus}
        </div>
      )}
    </div>
  );
}

export function MatrixCellPopover({ cell, onClose, acc, locale = "en" }) {
  if (!cell) return null;
  const bandColor =
    cell.band === "harmony" ? acc.secondary : cell.band === "caution" ? "#ff7b9c" : C.textMuted;
  const elA = elementColor(cell.pillarA?.element);
  const elB = elementColor(cell.pillarB?.element);

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${bandColor}44`,
        background: "rgba(255,255,255,0.04)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, color: C.textPrimary }}>{cell.label}</div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer" }}
          >
            ×
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: bandColor, marginBottom: 8 }}>
        {cell.score}% · {cell.bandLabel}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
          fontSize: 11,
          color: C.textSecondary,
        }}
      >
        <span>{formatAnimal(cell.pillarA?.animal, locale)}</span>
        <span style={{ color: C.textMuted }}>×</span>
        <span>{formatAnimal(cell.pillarB?.animal, locale)}</span>
        {cell.pillarA?.element && (
          <span style={{ color: elA.color }}>
            {elA.symbol} {cell.pillarA.element}
          </span>
        )}
        {cell.pillarB?.element && (
          <span style={{ color: elB.color }}>
            {elB.symbol} {cell.pillarB.element}
          </span>
        )}
      </div>
      {cell.branchInsight && (
        <p style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.65, margin: "0 0 8px" }}>
          {cell.branchInsight}
        </p>
      )}
      {cell.elementInsight && (
        <p style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.65, margin: 0 }}>
          {cell.elementInsight}
        </p>
      )}
    </div>
  );
}
